import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useFacility } from '../contexts/FacilityContext';
import { useCanMoveMoney, MONEY_ROLE_HINT } from '../contexts/AuthContext';
import {
  fetchOutstandingStatements,
  fetchBulkStatementData,
  createBulkStatements,
  fetchPatientCredits,
  applyPatientCredit,
} from '../services/api';
import { generateBulkStatements } from '../utils/generateStatement';
import { selectStatementClaims } from '../utils/statementClaims';
import './BulkStatementsPanel.css';

function fmtCurrency(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(String(d).slice(0, 10) + 'T12:00:00');
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtRange(from, to) {
  if (!from && !to) return '—';
  if (from === to || !to) return fmtDate(from);
  return `${fmtDate(from)} – ${fmtDate(to)}`;
}

export default function BulkStatementsPanel() {
  const { selectedFacility } = useFacility();
  const facilityId = selectedFacility?.id ?? null;

  const canMove = useCanMoveMoney();
  const [rows, setRows]           = useState([]);
  // Credit review modal — opened from a row, shows the transfers before posting any.
  const [creditModal, setCreditModal]   = useState(null);
  const [creditData, setCreditData]     = useState(null);
  const [creditBusy, setCreditBusy]     = useState(false);
  const [creditError, setCreditError]   = useState(null);
  const [recentDays, setRecentDays] = useState(30);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [view, setView]           = useState('ready');   // 'ready' | 'recent'
  const [search, setSearch]       = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [printing, setPrinting]   = useState(false);
  const [printError, setPrintError] = useState(null);
  const afterPrintRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      // The axios response interceptor unwraps to the server body: { success, data, recent_days }
      const res = await fetchOutstandingStatements(facilityId);
      setRows(res.data || []);
      if (res.recent_days) setRecentDays(res.recent_days);
    } catch {
      setError('Failed to load outstanding patient balances.');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  // Tear down any dangling afterprint listener on unmount
  useEffect(() => () => {
    if (afterPrintRef.current) window.removeEventListener('message', afterPrintRef.current);
  }, []);

  const readyRows  = useMemo(() => rows.filter((r) => !r.recently_billed), [rows]);
  const recentRows = useMemo(() => rows.filter((r) =>  r.recently_billed), [rows]);
  const viewRows   = view === 'ready' ? readyRows : recentRows;

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return viewRows;
    return viewRows.filter((r) =>
      `${r.first_name} ${r.last_name}`.toLowerCase().includes(q) ||
      r.account_number?.toLowerCase().includes(q));
  }, [viewRows, search]);

  // Default selection: everyone in "ready" EXCEPT rows whose credit covers what is owed —
  // mailing those a demand for money is the bug this guards. They stay visible and
  // selectable; they are just never billed by default. Nobody in "recent".
  //
  // Keyed on row_key, not patient_id: a row is a (patient, facility) pair. A patient seen
  // at two entities owes each separately and gets a statement from each, so their two rows
  // must be selectable independently.
  useEffect(() => {
    setSelectedIds(view === 'ready'
      ? new Set(readyRows.filter((r) => !r.credit_hold).map((r) => r.row_key))
      : new Set());
  }, [rows, view]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.row_key));
  const someVisibleSelected = visibleRows.some((r) => selectedIds.has(r.row_key)) && !allVisibleSelected;

  const toggleAll = () => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (allVisibleSelected) visibleRows.forEach((r) => next.delete(r.row_key));
    else                    visibleRows.forEach((r) => next.add(r.row_key));
    return next;
  });

  // Only count selections that are actually in the current view.
  const selectedInView = viewRows.filter((r) => selectedIds.has(r.row_key));
  const selectedTotal  = selectedInView.reduce((s, r) => s + (parseFloat(r.balance) || 0), 0);

  const openCredits = async (row) => {
    setCreditModal(row); setCreditData(null); setCreditError(null); setCreditBusy(true);
    try {
      const res = await fetchPatientCredits(row.patient_id);
      setCreditData(res.data);
    } catch {
      setCreditError('Could not load this account\'s credit.');
    } finally {
      setCreditBusy(false);
    }
  };

  const confirmApplyCredit = async () => {
    if (!creditModal) return;
    setCreditBusy(true); setCreditError(null);
    try {
      // Send the exact moves under review. The server re-derives the plan and rejects
      // the post if it no longer matches, so a stale modal cannot move money that has
      // since been spent elsewhere.
      await applyPatientCredit(creditModal.patient_id, { moves: creditData?.proposed_moves ?? [] });
      setCreditModal(null); setCreditData(null);
      await load();
    } catch (err) {
      setCreditError(err.response?.data?.error || 'Could not apply the credit.');
    } finally {
      setCreditBusy(false);
    }
  };

  const handlePrint = async () => {
    const patientIds = selectedInView.map((r) => r.patient_id);
    if (patientIds.length === 0) return;
    setPrinting(true); setPrintError(null);
    try {
      // Same facility the rows were scoped to, so each statement carries the entity that
      // is actually owed and only its claims.
      const res  = await fetchBulkStatementData(patientIds, facilityId);
      const data = res.data || [];
      const today = new Date().toISOString().slice(0, 10);

      // Claims that still carry a balance — or, when nothing is owed and this entity
      // is holding the patient's money, the credit-bearing ones, so a credit-only
      // account can still be sent the letter that tells them about it. See
      // utils/statementClaims.js.
      const items = [];
      const logPayloads = [];
      data.forEach((d) => {
        const claims = selectStatementClaims(d);
        if (claims.length === 0) return;

        items.push({ patient: d.patient, facility: d.facility, claims,
                     credit_summary: d.credit_summary });

        const serviceDates = claims.map((c) => String(c.service_date).slice(0, 10)).sort();
        const totals = claims.reduce((acc, c) => {
          acc.charges   += parseFloat(c.total_charge)           || 0;
          acc.insurance += parseFloat(c.insurance_payments)     || 0;
          acc.adj       += parseFloat(c.adjustments)            || 0;
          acc.ptResp    += parseFloat(c.patient_responsibility) || 0;
          acc.ptPaid    += parseFloat(c.patient_paid)           || 0;
          return acc;
        }, { charges: 0, insurance: 0, adj: 0, ptResp: 0, ptPaid: 0 });

        logPayloads.push({
          patient_id:                   d.patient.id || d.patient.patient_id,
          facility_id:                  d.facility?.facility_id,
          statement_date:               today,
          service_date_from:            serviceDates[0] || null,
          service_date_to:              serviceDates[serviceDates.length - 1] || null,
          total_charges:                totals.charges,
          total_insurance_payments:     totals.insurance,
          total_adjustments:            totals.adj,
          // What we actually billed the patient — the sheet's Balance Due, which is
          // net of payments they have already made. Logging the gross remit
          // responsibility here would overstate every partially-paid statement.
          total_patient_responsibility: Math.max(0, totals.ptResp - totals.ptPaid),
          claim_ids:                    claims.map((c) => c.id),
        });
      });

      // A patient billed at several facilities is never printed under a guessed
      // letterhead: with no facility filter the backend returns them for a choice.
      const needFacility = res.facility_required || [];
      if (items.length === 0) {
        setPrintError(needFacility.length > 0
          ? 'The selected patients have claims at more than one facility. Filter the list by facility, then print.'
          : 'None of the selected patients have a balance or a credit to report anymore.');
        return;
      }
      if (needFacility.length > 0) {
        setPrintError(`${needFacility.length} patient(s) with claims at more than one facility were left out. Filter by facility to print theirs.`);
      }

      // Log all statements once the user actually prints, then refresh the list so
      // freshly-billed patients move into the "Recently Sent" view.
      if (afterPrintRef.current) window.removeEventListener('message', afterPrintRef.current);
      const onAfterPrint = async (event) => {
        if (event.data?.type !== '__STMT_BULK_AFTER_PRINT__') return;
        window.removeEventListener('message', onAfterPrint);
        afterPrintRef.current = null;
        try {
          await createBulkStatements(logPayloads);
          await load();
        } catch {
          setPrintError('Statements printed but could not all be saved to history.');
        }
      };
      afterPrintRef.current = onAfterPrint;
      window.addEventListener('message', onAfterPrint);

      const win = generateBulkStatements(items, { statementDate: today });
      if (!win) {
        // Pop-up blocked — openPrintWindow already alerted; drop the listener.
        window.removeEventListener('message', onAfterPrint);
        afterPrintRef.current = null;
      }
    } catch (err) {
      setPrintError(err?.response?.data?.error || 'Failed to build statements. Please try again.');
    } finally {
      setPrinting(false);
    }
  };

  // With no facility filter a patient can appear more than once, so the column is what
  // tells the two rows apart. It shows the CLAIM facility, not where the patient is
  // registered.
  const showFacilityCol = !facilityId;

  return (
    <div className="bsp">
      {/* Ready / Recently-sent pills */}
      <div className="payments-controls">
        <div className="payments-tabs">
          <button
            className={`payments-tab ${view === 'ready' ? 'active' : ''}`}
            onClick={() => setView('ready')}
          >
            Ready to Send
            <span className="payments-tab-count">{readyRows.length}</span>
          </button>
          <button
            className={`payments-tab ${view === 'recent' ? 'active' : ''}`}
            onClick={() => setView('recent')}
            title={`Patients billed a statement within the last ${recentDays} days`}
          >
            Recently Sent
            <span className="payments-tab-count">{recentRows.length}</span>
          </button>
        </div>
        <div className="payments-search-wrap">
          <svg className="payments-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="payments-search"
            type="text"
            placeholder="Search patient, account #…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {view === 'ready' && readyRows.some((r) => r.credit_hold) && (
        <div className="bsp-note bsp-note-credit">
          {readyRows.filter((r) => r.credit_hold).length} patient(s) are holding credit that covers
          what they owe. They are <strong>not selected</strong> — sending a statement would demand money
          from an account that is square or in refund. Click the credit amount to review and apply it.
        </div>
      )}

      {view === 'recent' && recentRows.length > 0 && (
        <div className="bsp-note">
          These patients had a statement generated within the last {recentDays} days. They are held out of
          <strong> Ready to Send</strong> so you don&apos;t bill them twice — but you can still select and reprint here.
        </div>
      )}

      {loading ? (
        <div className="payments-loading"><div className="payments-spinner" /><span>Loading outstanding balances…</span></div>
      ) : error ? (
        <div className="payments-error">{error}<button className="payments-error-retry" onClick={load}>Retry</button></div>
      ) : visibleRows.length === 0 ? (
        <div className="payments-empty">
          <div className="payments-empty-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
          </div>
          <p className="payments-empty-title">
            {search
              ? 'No patients match your search'
              : view === 'ready'
                ? 'No patient balances ready to bill'
                : 'No statements sent recently'}
          </p>
          {!search && view === 'ready' && (
            <p className="payments-empty-sub">Patients with an outstanding balance will appear here once claims are adjudicated.</p>
          )}
        </div>
      ) : (
        <div className="payments-table-wrap">
          <table className="payments-table bsp-table">
            <thead>
              <tr>
                <th className="bsp-check-col">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
                <th>Patient</th>
                <th>Account #</th>
                {showFacilityCol && <th>Facility</th>}
                <th className="num">Balance Due</th>
                <th className="num">Credit</th>
                <th className="payments-td-center"># Claims</th>
                <th>Service Dates</th>
                <th>Last Statement</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const checked = selectedIds.has(r.row_key);
                return (
                  <tr
                    key={r.row_key}
                    className={`bsp-row ${checked ? 'bsp-row-selected' : ''}`}
                    onClick={() => toggle(r.row_key)}
                  >
                    <td className="bsp-check-col" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(r.row_key)} />
                    </td>
                    <td className="payments-td-payer">{r.last_name}, {r.first_name}</td>
                    <td className="payments-td-check">{r.account_number}</td>
                    {showFacilityCol && <td className="bsp-facility">{r.facility_name || '—'}</td>}
                    <td className="payments-td-amount bsp-balance">{fmtCurrency(r.balance)}</td>
                    <td className="payments-td-amount bsp-credit" onClick={(e) => e.stopPropagation()}>
                      {r.unapplied_credit > 0 ? (
                        <button
                          type="button"
                          className="bsp-credit-btn"
                          onClick={() => openCredits(r)}
                          title="Review and apply this credit"
                        >
                          {fmtCurrency(r.unapplied_credit)}
                          {r.credit_hold && <span className="bsp-credit-chip">Holds balance</span>}
                        </button>
                      ) : <span className="bsp-never">—</span>}
                    </td>
                    <td className="payments-td-center">{r.claim_count}</td>
                    <td>{fmtRange(r.service_from, r.service_to)}</td>
                    <td>
                      {r.last_statement_date ? (
                        <span className="bsp-last-stmt">
                          {fmtDate(r.last_statement_date)}
                          {r.recently_billed && <span className="bsp-recent-chip">Recent</span>}
                        </span>
                      ) : (
                        <span className="bsp-never">Never</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {printError && (
        <div className="payments-error" style={{ marginTop: 8 }}>{printError}</div>
      )}

      {creditModal && (
        <div className="bsp-modal-backdrop" onClick={() => !creditBusy && setCreditModal(null)}>
          <div className="bsp-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="bsp-modal-title">
              Apply credit — {creditModal.last_name}, {creditModal.first_name}
            </h3>

            {creditBusy && !creditData ? (
              <div className="payments-loading"><div className="payments-spinner" /><span>Loading…</span></div>
            ) : creditData ? (
              <>
                <div className="bsp-modal-figures">
                  <div><span>Open balance</span><strong>{fmtCurrency(creditData.open_balance)}</strong></div>
                  <div><span>Unapplied credit</span><strong>{fmtCurrency(creditData.unapplied_credit)}</strong></div>
                  <div><span>Net</span><strong>{fmtCurrency(creditData.net_due)}</strong></div>
                </div>

                {creditData.proposed_moves.length > 0 ? (
                  <>
                    <p className="bsp-modal-lead">
                      These transfers will be posted. Each one is two ledger entries — money leaves the
                      claim it was overpaid on and lands on the claim it pays — so nothing is invented
                      and either side can be reversed later.
                    </p>
                    <table className="bsp-modal-table">
                      <thead><tr><th>From claim</th><th>To claim</th><th className="num">Amount</th></tr></thead>
                      <tbody>
                        {creditData.proposed_moves.map((m, i) => (
                          <tr key={i}>
                            <td>{m.from_claim_number}</td>
                            <td>{m.to_claim_number}</td>
                            <td className="num">{fmtCurrency(m.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr><td colSpan={2}>Total</td><td className="num">{fmtCurrency(creditData.applicable)}</td></tr></tfoot>
                    </table>
                  </>
                ) : (
                  <p className="bsp-modal-lead">
                    Nothing can be transferred — this credit is at a facility where the patient owes
                    nothing. It is refundable rather than applicable.
                  </p>
                )}

                {creditData.pending_credit > 0 && (
                  <p className="bsp-modal-lead">
                    <strong>{fmtCurrency(creditData.pending_credit)}</strong> more is paid on claims not
                    generated yet. It is not credit: it waits for the charge on its own claim, so it is
                    neither transferred nor refundable until that claim is generated.
                  </p>
                )}

                {creditData.refundable > 0 && (
                  <p className="bsp-modal-refund">
                    <strong>{fmtCurrency(creditData.refundable)}</strong> cannot be applied and is owed back
                    to the patient. Credit never moves between facilities — each is a separate billing
                    entity with its own NPI and tax ID, so that has to be a real refund.
                  </p>
                )}

                {creditData.by_facility?.length > 1 && (
                  <table className="bsp-modal-table">
                    <thead><tr><th>Facility</th><th className="num">Owed</th><th className="num">Credit</th></tr></thead>
                    <tbody>
                      {creditData.by_facility.map((f) => (
                        <tr key={f.facility_id}>
                          <td>{f.facility_name}</td>
                          <td className="num">{fmtCurrency(f.open_balance)}</td>
                          <td className="num">{fmtCurrency(f.unapplied_credit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ) : null}

            {creditError && <div className="payments-error" style={{ marginTop: 8 }}>{creditError}</div>}

            <div className="bsp-modal-actions">
              <button type="button" className="bsp-btn-secondary"
                      onClick={() => setCreditModal(null)} disabled={creditBusy}>
                Cancel
              </button>
              <button type="button" className="bsp-btn-primary"
                      onClick={confirmApplyCredit}
                      disabled={creditBusy || !creditData?.proposed_moves?.length || !canMove}
                      title={canMove ? undefined : MONEY_ROLE_HINT}>
                {creditBusy ? 'Applying…' : `Apply ${fmtCurrency(creditData?.applicable ?? 0)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky action bar */}
      {!loading && !error && (
        <div className="bsp-actionbar">
          <div className="bsp-summary">
            <strong>{selectedInView.length}</strong> selected
            {selectedInView.length > 0 && <> · <strong>{fmtCurrency(selectedTotal)}</strong> total</>}
          </div>
          <button
            className="payments-btn-record bsp-print-btn"
            onClick={handlePrint}
            disabled={printing || selectedInView.length === 0}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            {printing
              ? 'Preparing…'
              : `Print ${selectedInView.length || ''} Statement${selectedInView.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}
    </div>
  );
}
